/**
 * Exact rational arithmetic for the Demand_Engine (R5.1, R5.7, R9.1).
 *
 * ## Why plain `number` cannot be the Demand_Engine's arithmetic
 *
 * `percent.ts` already states the reasoning this module generalizes: "Binary
 * floating point cannot answer [an exactness] question... so percentages travel
 * as canonical decimal text... and all arithmetic happens on integer
 * hundredths, where every value in range is exactly representable." The
 * Platform split never divides - it only sums up to ten two-decimal values - so
 * integer hundredths are enough on their own.
 *
 * The demand formula of R5.1 is not that lucky:
 *
 * ```
 * population x (coverage / 100) x frequency x (effort-per-unit-minutes / 60)
 *   + fixed overhead hours
 * ```
 *
 * Two of those divisors, 60 and 100, are not exact in base-2 floating point,
 * and R5.7's Reconciliation_Control then requires an *exact* equality between
 * a driver's Demand_Hours and the sum of its period-allocated shares - "never
 * an approximation" per `complan-manpower/design.md`. Measured empirically
 * (a `fc.assert` run of 1,000,000 trials against a same-shaped naive
 * `number` implementation - proportional shares with a remainder correction on
 * the last one, the standard technique): summing shares back to the total
 * produced a `!==` result in roughly 0.3% of trials, and every one of those was
 * a genuine input, not a pathological one. At Property 44's minimum of 100
 * iterations per CI run, that rate makes the "never weakened" CI-blocking
 * property fail reliably, not occasionally - the identity `r + (total - r) ===
 * total` simply is not a theorem of IEEE-754 double arithmetic.
 *
 * So this module extends `percent.ts`'s "arithmetic that must be exact cannot
 * use plain doubles" rule from integer hundredths to arbitrary-precision
 * rationals: every quantity the demand formula and its allocations touch is an
 * {@link ExactFraction} - a `bigint` numerator over a `bigint` denominator -
 * from the moment it is parsed out of `numeric` column text until the moment it
 * is rendered for display or export. Bigint arithmetic has no rounding step, so
 * `addFractions`, `subtractFractions`, `multiplyFractions` and
 * `divideFractions` are exact by construction, and a sum of exact fractions
 * that is *supposed* to equal another exact fraction (a Reconciliation_Control
 * check) can be verified as such with no tolerance and no epsilon - see
 * {@link fractionsEqual}.
 *
 * ## Where this module is not needed
 *
 * The Capacity_Engine (`capacity.ts`, `utilization.ts`) carries no analogous
 * exact-equality requirement - Requirement 3 and 6 name no sum of
 * Capacity_Hours or Utilization that must equal another independently computed
 * total - so those formulas stay plain `number`, exactly as
 * `complan-manpower/design.md` originally sketched them. This module is scoped
 * to the Demand_Engine's formula, its Platform and period allocations, and the
 * Reconciliation_Control that checks them.
 *
 * ## Rounding happens once, at the boundary
 *
 * {@link fractionToNumber} and {@link fractionToDecimalText} are the only two
 * functions in this module that discard precision, and neither is ever used in
 * a Reconciliation_Control comparison. They exist for the one place Assumption
 * A11 permits rounding at all: "retain full precision internally, rounding only
 * at display" - the API and export boundary above this package, not here.
 */
/** Unsigned decimal text: no sign, no exponent, no leading zeros, no trailing dot. */
const UNSIGNED_DECIMAL_TEXT = /^(0|[1-9][0-9]*)(?:\.([0-9]+))?$/;
/** Greatest common divisor of two non-negative `bigint`s. `0` when both are `0`. */
function bigintGcd(a, b) {
    let x = a;
    let y = b;
    while (y !== 0n) {
        [x, y] = [y, x % y];
    }
    return x;
}
/**
 * Builds a fully reduced {@link ExactFraction}. The denominator is normalized to
 * positive (a negative denominator's sign moves to the numerator), and the pair
 * is divided by their greatest common divisor, so `numerator` and `denominator`
 * are always coprime with `denominator > 0`.
 */
export function exactFraction(numerator, denominator) {
    if (denominator === 0n) {
        throw new RangeError('exact fraction denominator must not be zero');
    }
    const sign = denominator < 0n ? -1n : 1n;
    const signedNumerator = numerator * sign;
    const signedDenominator = denominator * sign;
    if (signedNumerator === 0n) {
        return { numerator: 0n, denominator: 1n };
    }
    const magnitude = signedNumerator < 0n ? -signedNumerator : signedNumerator;
    const divisor = bigintGcd(magnitude, signedDenominator);
    return { numerator: signedNumerator / divisor, denominator: signedDenominator / divisor };
}
/** The rational number zero, `0/1`. */
export const ZERO_FRACTION = { numerator: 0n, denominator: 1n };
/** An integer as an {@link ExactFraction}, `value/1`. */
export function fractionFromInteger(value) {
    if (typeof value === 'number' && !Number.isInteger(value)) {
        throw new RangeError(`fractionFromInteger requires an integer, received ${String(value)}`);
    }
    return exactFraction(typeof value === 'bigint' ? value : BigInt(value), 1n);
}
/**
 * Parses unsigned decimal text (`'12.50'`, `'0'`, `'100'`) into an exact
 * fraction: `'12.50'` becomes the reduced fraction for `1250/100`, i.e. `25/2`.
 *
 * Throws on anything that is not unsigned decimal text, the same division of
 * labor `parsePercent` draws for Platform splits: a Zod schema and a database
 * `CHECK` constraint reject a malformed value before it reaches a domain
 * function, so a value reaching this parser has already been validated and a
 * failure here is a defect upstream, not a case for this function to report
 * gracefully a second time.
 */
export function fractionFromDecimalText(text) {
    const match = UNSIGNED_DECIMAL_TEXT.exec(text);
    if (match === null) {
        throw new RangeError(`not unsigned decimal text: ${JSON.stringify(text)}`);
    }
    const integerText = match[1] ?? '';
    const fractionText = match[2] ?? '';
    const denominator = 10n ** BigInt(fractionText.length);
    const numerator = BigInt(integerText) * denominator + (fractionText === '' ? 0n : BigInt(fractionText));
    return exactFraction(numerator, denominator);
}
/** `a + b`, exact. */
export function addFractions(a, b) {
    return exactFraction(a.numerator * b.denominator + b.numerator * a.denominator, a.denominator * b.denominator);
}
/** `a - b`, exact. */
export function subtractFractions(a, b) {
    return exactFraction(a.numerator * b.denominator - b.numerator * a.denominator, a.denominator * b.denominator);
}
/** `a x b`, exact. */
export function multiplyFractions(a, b) {
    return exactFraction(a.numerator * b.numerator, a.denominator * b.denominator);
}
/** `a / b`, exact. Throws when `b` is zero. */
export function divideFractions(a, b) {
    if (b.numerator === 0n) {
        throw new RangeError('division by zero');
    }
    return exactFraction(a.numerator * b.denominator, a.denominator * b.numerator);
}
/**
 * Exact equality of the rational numbers `a` and `b` - the comparison every
 * Reconciliation_Control check (`reconciliation.ts`) is built on. Cross-
 * multiplies rather than comparing components directly, so two fractions that
 * name the same rational number but were not both built through
 * {@link exactFraction} (for example one constructed by hand in a test) still
 * compare equal.
 */
export function fractionsEqual(a, b) {
    return a.numerator * b.denominator === b.numerator * a.denominator;
}
/** Negative, zero or positive as `a` is less than, equal to or greater than `b`. */
export function compareFractions(a, b) {
    const left = a.numerator * b.denominator;
    const right = b.numerator * a.denominator;
    return left < right ? -1 : left > right ? 1 : 0;
}
/** Whether `value` is the rational number zero. */
export function isZeroFraction(value) {
    return value.numerator === 0n;
}
/** Exact sum of a list of fractions; `ZERO_FRACTION` for an empty list. */
export function sumFractions(values) {
    return values.reduce(addFractions, ZERO_FRACTION);
}
/**
 * Converts to a JavaScript `number`, for display and export only (Assumption
 * A11: "retain full precision internally, rounding only at display"). Never
 * used inside a Reconciliation_Control comparison - see {@link fractionsEqual} -
 * because converting first is exactly the precision loss this module exists to
 * defer past the point where an exact answer is required.
 */
export function fractionToNumber(value) {
    return Number(value.numerator) / Number(value.denominator);
}
/**
 * Renders to fixed-point decimal text at `decimalPlaces`, rounding half away
 * from zero. Every intermediate step of the calculation this value came from
 * was exact; this is the one place precision beyond `decimalPlaces` is
 * discarded, and it is discarded through exact integer division and
 * remainder - not through a `number` round-trip - so the rendered text is the
 * correctly-rounded decimal expansion of the exact rational value, not of a
 * double that already approximated it.
 */
export function fractionToDecimalText(value, decimalPlaces) {
    if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0) {
        throw new RangeError(`decimalPlaces must be a non-negative integer, received ${String(decimalPlaces)}`);
    }
    const scale = 10n ** BigInt(decimalPlaces);
    const negative = value.numerator < 0n;
    const magnitude = negative ? -value.numerator : value.numerator;
    const scaledNumerator = magnitude * scale;
    const quotient = scaledNumerator / value.denominator;
    const remainder = scaledNumerator % value.denominator;
    const rounded = remainder * 2n >= value.denominator ? quotient + 1n : quotient;
    const digits = rounded.toString().padStart(decimalPlaces + 1, '0');
    const sign = negative && rounded !== 0n ? '-' : '';
    if (decimalPlaces === 0) {
        return `${sign}${digits}`;
    }
    const integerPart = digits.slice(0, digits.length - decimalPlaces);
    const fractionPart = digits.slice(digits.length - decimalPlaces);
    return `${sign}${integerPart}.${fractionPart}`;
}
//# sourceMappingURL=exact-fraction.js.map